import React, { useState, useEffect, useRef } from "react";

/**
 * Streams an HTML string without flashing partial tags.
 *
 * Props
 * - htmlString      : full HTML string to reveal
 * - speed           : delay between frames in **milliseconds** (default: 20 ms)
 * - startCondition  : if false, renders the full html immediately
 * - className       : optional wrapper class
 * - streamIncrement : visible characters revealed per frame (default: 3)
 */
const StreamedHTML = ({
  htmlString = "",
  speed = 20,
  startCondition = true,
  className = "",
  streamIncrement = 3,
}) => {
  const [displayedHtml, setDisplayedHtml] = useState("");
  const intervalIdRef = useRef(null);
  const currentIndexRef = useRef(0);
  const hasAnimatedRef = useRef(false); // Track if animation has completed
  const previousHtmlStringRef = useRef(""); // Track previous htmlString to detect changes

  /**
   * Jump forward by `increment` _visible_ characters.
   * If we encounter an opening `<`, we skip the entire tag so we never
   * paint half‑rendered markup to the screen.
   */
  const nextIndex = (str, idx, increment) => {
    let next = idx;
    let revealed = 0;

    while (next < str.length && revealed < increment) {
      if (str[next] === "<") {
        const close = str.indexOf(">", next);
        next = close !== -1 ? close + 1 : str.length; // jump past the tag
      } else {
        next += 1;
        revealed += 1;
      }
    }
    return next;
  };

  useEffect(() => {
    // clear any existing timer first
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    if (!htmlString) {
      setDisplayedHtml("");
      currentIndexRef.current = 0;
      hasAnimatedRef.current = false;
      previousHtmlStringRef.current = "";
      return;
    }

    // render immediately when streaming is disabled
    if (!startCondition) {
      setDisplayedHtml(htmlString);
      currentIndexRef.current = htmlString.length;
      // Don't set hasAnimatedRef here - allow animation if startCondition becomes true later
      return;
    }

    // Check if htmlString has changed from previous
    const htmlStringChanged = previousHtmlStringRef.current !== htmlString;
    
    // If animation already completed and htmlString hasn't changed, don't restart
    if (hasAnimatedRef.current && !htmlStringChanged) {
      // Ensure content is displayed even if animation completed
      if (displayedHtml !== htmlString) {
        setDisplayedHtml(htmlString);
      }
      return;
    }
    
    // Update the previous htmlString reference
    previousHtmlStringRef.current = htmlString;

    // fresh stream - reset state
    setDisplayedHtml("");
    currentIndexRef.current = 0;

    const increment = Math.max(1, parseInt(streamIncrement, 10) || 1); // radix 10!
    const delay = Math.max(4, parseInt(speed, 10) || 20); // browser minimum ≈4 ms

    // Use requestAnimationFrame to ensure DOM is ready before starting animation
    const startAnimation = () => {
      intervalIdRef.current = setInterval(() => {
        if (currentIndexRef.current < htmlString.length) {
          currentIndexRef.current = nextIndex(htmlString, currentIndexRef.current, increment);
          setDisplayedHtml(htmlString.substring(0, currentIndexRef.current));
        } else {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
          hasAnimatedRef.current = true; // Mark animation as completed
        }
      }, delay);
    };

    // Small delay to ensure component is fully mounted and visible
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(startAnimation);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);
    };
  }, [htmlString, speed, startCondition, streamIncrement]);

  return <div className={className} dangerouslySetInnerHTML={{ __html: displayedHtml }} />;
};

export default StreamedHTML;
